import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    EmbeddedCheckout,
    EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js'

import {
    formatApplicationFee,
    useApplicationGate,
} from '../../hooks/Outreach/useApplicationGate'
import { stripePromise } from '../../helpers/stripe'
import { retryWithBackoff } from '../../utils/retryWithBackoff'

const useStyles = makeStyles((theme: Theme) => ({
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(4, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    formCard: {
        maxWidth: '640px',
        width: '100%',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        borderRadius: '12px',
    },
    cardHeader: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
    },
    cardTitle: {
        fontSize: '1.75rem',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
    },
    cardSubtitle: {
        fontSize: '0.875rem',
        color: '#6b7280',
    },
    cardContent: {
        padding: theme.spacing(3),
        backgroundColor: '#ffffff',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
    },
    checkoutContainer: {
        width: '100%',
        '& iframe': {
            width: '100% !important',
            border: 'none',
        },
    },
    centeredState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        minHeight: '180px',
    },
}))

export const ApplicationGateForm: React.FC = () => {
    const classes = useStyles()
    const { token } = useParams<{ token: string }>()
    const location = useLocation()
    const sessionId = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return params.get('session_id')
    }, [location.search])
    const confirmingPayment = !!sessionId
    const [clientSecret, setClientSecret] = useState<string | null>(null)
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const [isStartingCheckout, setIsStartingCheckout] = useState(false)
    const [retryMessage, setRetryMessage] = useState<string | null>(null)
    const checkoutAttemptKey = useRef<string | null>(null)

    const { gate, isLoading, error, createCheckout } = useApplicationGate(
        token,
        {
            pollUntilUnlocked: confirmingPayment,
            sessionId,
        },
    )

    const startCheckout = useCallback(async () => {
        if (!token || !gate?.requires_checkout || confirmingPayment) {
            return
        }

        setCheckoutError(null)
        setRetryMessage(null)
        setIsStartingCheckout(true)

        try {
            const response = await retryWithBackoff(
                () => createCheckout.mutateAsync(),
                {
                    onRetry: (_error, attempt, delayMs) => {
                        setRetryMessage(
                            `Checkout unavailable. Retrying (${attempt}/3) in ${Math.round(
                                delayMs / 1000,
                            )}s...`,
                        )
                    },
                },
            )
            setClientSecret(response.data.client_secret)
            setRetryMessage(null)
        } catch (checkoutFailure) {
            const apiError = (checkoutFailure as {
                response?: { data?: { error?: string } }
            })?.response?.data?.error
            setCheckoutError(
                apiError || 'Unable to start checkout. Please try again later.',
            )
            setRetryMessage(null)
        } finally {
            setIsStartingCheckout(false)
        }
    }, [token, gate?.requires_checkout, confirmingPayment, createCheckout])

    useEffect(() => {
        if (!gate?.is_unlocked || !gate.docuseal_signing_url) {
            return
        }
        window.location.href = gate.docuseal_signing_url
    }, [gate?.is_unlocked, gate?.docuseal_signing_url])

    useEffect(() => {
        const key = `${token}:${gate?.requires_checkout}:${confirmingPayment}`
        if (
            !token ||
            !gate?.requires_checkout ||
            confirmingPayment ||
            clientSecret ||
            checkoutError ||
            checkoutAttemptKey.current === key
        ) {
            return
        }
        checkoutAttemptKey.current = key
        startCheckout()
    }, [
        token,
        gate?.requires_checkout,
        confirmingPayment,
        clientSecret,
        checkoutError,
        startCheckout,
    ])

    if (isLoading && !gate) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent className={classes.centeredState}>
                        <CircularProgress />
                        <Typography variant="body1">
                            Loading application...
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (error || !gate) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent className={classes.centeredState}>
                        <Typography variant="h6" color="error">
                            Invalid or expired application link
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Contact the property if you need a new link.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (gate.application_status === 'submitted') {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardTitle}>
                            Application submitted
                        </Typography>
                        <Typography className={classes.cardSubtitle}>
                            {gate.property_name}
                        </Typography>
                    </Box>
                    <CardContent className={classes.cardContent}>
                        <Typography variant="body1">
                            {gate.message ||
                                'Your application has already been submitted.'}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (confirmingPayment && !gate.is_unlocked) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardTitle}>
                            Confirming payment
                        </Typography>
                        <Typography className={classes.cardSubtitle}>
                            {gate.property_name}
                        </Typography>
                    </Box>
                    <CardContent className={classes.centeredState}>
                        <CircularProgress />
                        <Typography variant="body1">
                            Payment received. Unlocking your application...
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (gate.is_unlocked) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent className={classes.centeredState}>
                        <CircularProgress />
                        <Typography variant="body1">
                            Opening your application form...
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (checkoutError) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent className={classes.centeredState}>
                        <Typography variant="h6" color="error">
                            {checkoutError}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={isStartingCheckout}
                            onClick={() => {
                                checkoutAttemptKey.current = null
                                setCheckoutError(null)
                                startCheckout()
                            }}
                        >
                            Try again
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (!stripePromise) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent className={classes.centeredState}>
                        <Typography variant="h6" color="error">
                            Payment is not configured for this environment.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    return (
        <Box className={classes.pageContainer}>
            <Card className={classes.formCard}>
                <Box className={classes.cardHeader}>
                    <Typography className={classes.cardTitle}>
                        Application fee
                    </Typography>
                    <Typography className={classes.cardSubtitle}>
                        {gate.property_name}
                    </Typography>
                </Box>
                <CardContent className={classes.cardContent}>
                    <Typography variant="body1" gutterBottom>
                        Hi {gate.prospect_name}, pay the application fee to
                        continue to your rental application.
                    </Typography>
                    <Typography variant="h5" style={{ marginBottom: 24 }}>
                        {formatApplicationFee(gate.application_fee_cents)}
                    </Typography>
                    {!clientSecret || isStartingCheckout ? (
                        <Box className={classes.centeredState}>
                            <CircularProgress />
                            <Typography variant="body2" color="textSecondary">
                                {retryMessage || 'Preparing secure checkout...'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box className={classes.checkoutContainer}>
                            <EmbeddedCheckoutProvider
                                stripe={stripePromise}
                                options={{ clientSecret }}
                            >
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}
